import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import api, { formatErr } from "@/lib/api";
import { useDialog } from "@/context/DialogProvider";
import { useSite } from "@/context/SiteContext";
import { Plus, Trash2, Calendar, Pencil, QrCode, Archive, ArchiveRestore, MoreHorizontal } from "lucide-react";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import EquipmentQRDialog from "@/components/EquipmentQRDialog";
import CleaningLogTab from "@/pages/CleaningLogTab";
import { EQUIPMENT_TYPE_PRESETS, findPreset } from "@/lib/equipmentTypes";

const intervalToDays = (val, unit) => (unit === "months" ? Number(val || 0) * 30 : Number(val || 0));

const computeNextDue = (lastIso, type) => {
  if (!lastIso || !type?.requires_calibration) return "";
  const d = intervalToDays(type.default_calibration_interval_days, type.calibration_interval_unit || "days");
  if (!d) return "";
  const dt = new Date(lastIso);
  if (isNaN(dt.getTime())) return "";
  dt.setDate(dt.getDate() + d);
  return dt.toISOString().slice(0, 10);
};

export default function Equipment() {
  const { confirm } = useDialog();
  const { activeSiteId, isAllSites, isSingleSite, visibleSites } = useSite();
  const [tab, setTab] = useState("equipment");
  const [types, setTypes] = useState([]);
  const [items, setItems] = useState([]);
  const [calItems, setCalItems] = useState([]);
  const [sites, setSites] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [show, setShow] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({});
  const [err, setErr] = useState("");
  const [qrEquipment, setQrEquipment] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [defaultMode, setDefaultMode] = useState("scheduled");

  const load = async () => {
    const [t, e, s, f, c, p] = await Promise.all([
      api.get("/equipment-types"),
      api.get("/equipment", { params: { include_archived: showArchived } }),
      api.get("/sites"),
      api.get("/facilities"),
      api.get("/calibrated-equipment", { params: { include_archived: showArchived } }),
      api.get("/tenant/profile").catch(() => ({ data: {} })),
    ]);
    setTypes(t.data.equipment_types || []);
    setItems(e.data.equipment || []);
    setSites(s.data.sites || []);
    setFacilities(f.data.facilities || []);
    setCalItems(c.data.items || []);
    setDefaultMode(p.data?.profile?.default_monitoring_mode || "scheduled");
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeSiteId, showArchived]);

  const siteFacilities = useMemo(
    () => facilities.filter((f) => f.site_id === form.site_id),
    [facilities, form.site_id]
  );
  const selectedType = useMemo(
    () => types.find((t) => t.id === form.equipment_type_id),
    [types, form.equipment_type_id]
  );
  // Vehicles (Delivery Truck / Cold Truck) live at a site rather than
  // inside a facility, so the facility dropdown is hidden + optional.
  const VEHICLE_TYPE_NAMES = ["delivery truck", "cold truck"];
  const isVehicleType = (t) => t && VEHICLE_TYPE_NAMES.includes((t.name || "").toLowerCase());
  const isVehicle = isVehicleType(selectedType);

  // ---------- Equipment Type modal ----------
  const openType = () => {
    setEditingId(null);
    setForm({
      name: "", description: "",
      requires_calibration: false,
      default_calibration_interval_days: 365, calibration_interval_unit: "days", _intervalValue: 365,
      has_operating_range: false, parameter_name: "", parameter_unit: "", min_value: "", max_value: "",
      collection_frequency_per_day: 1,
    });
    setShow("type"); setErr("");
  };
  const editType = (t) => {
    const unit = t.calibration_interval_unit || "days";
    const days = t.default_calibration_interval_days || 365;
    const value = unit === "months" ? Math.max(1, Math.round(days / 30)) : days;
    setEditingId(t.id);
    setForm({
      name: t.name || "", description: t.description || "",
      requires_calibration: !!t.requires_calibration,
      default_calibration_interval_days: days, calibration_interval_unit: unit, _intervalValue: value,
      has_operating_range: !!t.has_operating_range,
      parameter_name: t.parameter_name || "", parameter_unit: t.parameter_unit || "",
      min_value: t.min_value ?? "", max_value: t.max_value ?? "",
      collection_frequency_per_day: t.collection_frequency_per_day ?? 1,
    });
    setShow("type"); setErr("");
  };

  // ---------- Calibrated equipment modal ----------
  const openCal = () => {
    setEditingId(null);
    const presetSite = !isAllSites ? activeSiteId : (visibleSites[0]?.id || sites[0]?.id || "");
    setForm({
      site_id: presetSite, facility_id: "",
      name: "", serial_number: "", model_number: "",
      last_calibrated_at: "", last_calibrated_by: "",
      calibration_interval_value: 12,
      calibration_interval_unit: "months",
      calibration_certificate_url: "",
    });
    setShow("cal"); setErr("");
  };
  const editCal = (c) => {
    setEditingId(c.id);
    setForm({
      site_id: c.site_id || "", facility_id: c.facility_id || "",
      name: c.name || "", serial_number: c.serial_number || "",
      model_number: c.model_number || "",
      last_calibrated_at: c.last_calibrated_at ? c.last_calibrated_at.slice(0, 10) : "",
      last_calibrated_by: c.last_calibrated_by || "",
      calibration_interval_value: c.calibration_interval_value ?? 12,
      calibration_interval_unit: c.calibration_interval_unit || "months",
      calibration_certificate_url: c.calibration_certificate_url || "",
    });
    setShow("cal"); setErr("");
  };

  // ---------- Equipment modal ----------
  const openEq = () => {
    setEditingId(null);
    // Pre-fill site_id from the active site context (locked when single-site).
    const presetSite = !isAllSites ? activeSiteId : (visibleSites[0]?.id || sites[0]?.id || "");
    setForm({
      site_id: presetSite, facility_id: "",
      equipment_type_id: types[0]?.id || "",
      name: "", serial_number: "", model_number: "", location: "",
      monitoring_mode: defaultMode,
      last_calibrated_at: "", last_calibrated_by: "",
      next_calibration_due: "", next_due_manual_override: false,
      calibration_certificate_url: "",
    });
    setShow("eq"); setErr("");
  };
  const editEq = (e) => {
    setEditingId(e.id);
    setForm({
      site_id: e.site_id || "", facility_id: e.facility_id || "",
      equipment_type_id: e.equipment_type_id || "",
      name: e.name || "", serial_number: e.serial_number || "",
      model_number: e.model_number || "", location: e.location || "",
      monitoring_mode: e.monitoring_mode || "scheduled",
      last_calibrated_at: e.last_calibrated_at ? e.last_calibrated_at.slice(0, 10) : "",
      last_calibrated_by: e.last_calibrated_by || "",
      next_calibration_due: e.next_calibration_due ? e.next_calibration_due.slice(0, 10) : "",
      next_due_manual_override: !!e.next_due_manual_override,
      calibration_certificate_url: e.calibration_certificate_url || "",
    });
    setShow("eq"); setErr("");
  };

  const submit = async (ev) => {
    ev.preventDefault(); setErr("");
    try {
      if (show === "type") {
        const unit = form.calibration_interval_unit || "days";
        const value = Number(form._intervalValue || 0) || 1;
        const days = unit === "months" ? value * 30 : value;
        const payload = {
          name: form.name,
          description: form.description || "",
          requires_calibration: !!form.requires_calibration,
          default_calibration_interval_days: days,
          calibration_interval_unit: unit,
          has_operating_range: form.has_operating_range !== false,
          parameter_name: form.parameter_name || "",
          parameter_unit: form.parameter_unit || "",
          min_value: form.min_value !== "" ? Number(form.min_value) : null,
          max_value: form.max_value !== "" ? Number(form.max_value) : null,
          collection_frequency_per_day: Math.max(1, parseInt(form.collection_frequency_per_day, 10) || 1),
        };
        if (editingId) await api.put(`/equipment-types/${editingId}`, payload);
        else await api.post("/equipment-types", payload);
      } else if (show === "cal") {
        if (!form.site_id) { setErr("Please select a site."); return; }
        if (!form.facility_id) { setErr("Please select a facility."); return; }
        if (!(form.name || "").trim()) { setErr("Name is required."); return; }
        if (!(form.serial_number || "").trim()) { setErr("Serial number is required."); return; }
        if (!form.last_calibrated_at) { setErr("Last calibrated date is required."); return; }
        const payload = {
          site_id: form.site_id,
          facility_id: form.facility_id,
          name: form.name.trim(),
          serial_number: form.serial_number.trim(),
          model_number: (form.model_number || "").trim(),
          last_calibrated_at: new Date(form.last_calibrated_at).toISOString(),
          last_calibrated_by: form.last_calibrated_by || "",
          calibration_interval_value: Number(form.calibration_interval_value) || 1,
          calibration_interval_unit: form.calibration_interval_unit || "months",
          calibration_certificate_url: form.calibration_certificate_url || null,
        };
        if (editingId) await api.put(`/calibrated-equipment/${editingId}`, payload);
        else await api.post("/calibrated-equipment", payload);
      } else {
        // Hard-validate required selections — facility + serial are now required
        // (facility is skipped for vehicles which live at a site directly).
        if (!form.site_id) { setErr("Please select a site."); return; }
        if (!form.equipment_type_id) { setErr("Please select an equipment type."); return; }
        if (!isVehicle && !form.facility_id) { setErr("Please select a facility (add one in Sites if none exist for this site)."); return; }
        if (!(form.serial_number || "").trim()) { setErr(isVehicle ? "Vehicle number is required." : "Serial number is required."); return; }
        const payload = {
          site_id: form.site_id,
          facility_id: isVehicle ? null : form.facility_id,
          equipment_type_id: form.equipment_type_id,
          name: form.name,
          serial_number: form.serial_number.trim(),
          model_number: (form.model_number || "").trim(),
          location: form.location || "",
          monitoring_mode: form.monitoring_mode || "scheduled",
          last_calibrated_by: form.last_calibrated_by || "",
          next_due_manual_override: !!form.next_due_manual_override,
          calibration_certificate_url: form.calibration_certificate_url || null,
        };
        if (form.last_calibrated_at) payload.last_calibrated_at = new Date(form.last_calibrated_at).toISOString();
        else payload.last_calibrated_at = null;
        // Only send next_calibration_due if user manually overrode it.
        if (form.next_due_manual_override && form.next_calibration_due) {
          payload.next_calibration_due = new Date(form.next_calibration_due).toISOString();
        } else {
          payload.next_calibration_due = null; // server will auto-compute
        }
        if (editingId) await api.put(`/equipment/${editingId}`, payload);
        else await api.post("/equipment", payload);
      }
      setShow(null); setEditingId(null); load();
    } catch (e) { setErr(formatErr(e.response?.data?.detail)); }
  };

  const del = async (kind, id, label) => {
    const titles = { type: "Delete equipment type?", eq: "Delete equipment?", cal: "Delete calibrated equipment?" };
    const paths = { type: `/equipment-types/${id}`, eq: `/equipment/${id}`, cal: `/calibrated-equipment/${id}` };
    if (!await confirm({ title: titles[kind] || "Delete?", message: `Remove "${label}". This cannot be undone.`, danger: true, confirmLabel: "Delete" })) return;
    await api.delete(paths[kind]);
    load();
  };

  const archiveEquipment = async (e) => {
    if (!await confirm({
      title: `Archive ${e.name}?`,
      message: "Archived equipment is hidden from monitoring and exports but kept for historical readings. You can restore it later via the \"Show archived\" toggle. Any CCPs linked to this equipment will also be archived.",
      confirmLabel: "Archive",
    })) return;
    try {
      await api.post(`/equipment/${e.id}/archive`);
      load();
    } catch (ex) {
      setErr(ex.response?.data?.detail || "Couldn't archive");
    }
  };
  const unarchiveEquipment = async (e) => {
    if (!await confirm({
      title: `Restore ${e.name}?`,
      message: "This equipment will become active again. Its previously-linked CCPs stay archived — recreate or re-link them through the CCP page.",
      confirmLabel: "Restore",
    })) return;
    try {
      await api.post(`/equipment/${e.id}/unarchive`);
      load();
    } catch (ex) {
      setErr(ex.response?.data?.detail || "Couldn't restore");
    }
  };

  const archiveCal = async (c) => {
    if (!await confirm({
      title: `Archive ${c.name}?`,
      message: "Archived instruments are hidden from the active register but kept for the historical audit trail — use this when you retire an instrument and buy a new one. You can restore it later via the \"Show archived\" toggle.",
      confirmLabel: "Archive",
    })) return;
    try {
      await api.post(`/calibrated-equipment/${c.id}/archive`);
      load();
    } catch (ex) {
      setErr(ex.response?.data?.detail || "Couldn't archive");
    }
  };
  const unarchiveCal = async (c) => {
    if (!await confirm({
      title: `Restore ${c.name}?`,
      message: "This calibrated instrument will become active again in the register.",
      confirmLabel: "Restore",
    })) return;
    try {
      await api.post(`/calibrated-equipment/${c.id}/unarchive`);
      load();
    } catch (ex) {
      setErr(ex.response?.data?.detail || "Couldn't restore");
    }
  };

  const siteName = (id) => sites.find((s) => s.id === id)?.name || "—";
  const facilityName = (id) => facilities.find((f) => f.id === id)?.name || "—";
  const typeName = (id) => types.find((t) => t.id === id)?.name || "—";
  const isOverdue = (dt) => dt && new Date(dt) < new Date();

  // Live preview of computed next-due while filling the equipment form
  const previewNextDue = !form.next_due_manual_override && form.last_calibrated_at && selectedType?.requires_calibration
    ? computeNextDue(form.last_calibrated_at, selectedType)
    : "";

  return (
    <div className="vb-fade-in" data-testid="equipment-page">
      <div className="mb-6">
        <h1 className="vb-heading text-3xl mb-1" style={{ color: "var(--vb-text-2)" }}>Equipment & calibration</h1>
        <p className="opacity-70">Customer-defined equipment types, individual instances, and calibration tracking for measuring devices.</p>
      </div>

      <div className="flex justify-between items-center mb-4 flex-wrap gap-3">
        <div className="vb-tabs" role="tablist">
          <button role="tab" aria-selected={tab === "equipment"} className={`vb-tab ${tab === "equipment" ? "active" : ""}`} onClick={() => setTab("equipment")} data-testid="tab-eq">Equipment</button>
          <button role="tab" aria-selected={tab === "types"} className={`vb-tab ${tab === "types" ? "active" : ""}`} onClick={() => setTab("types")} data-testid="tab-types">Types</button>
          <button role="tab" aria-selected={tab === "calibrated"} className={`vb-tab ${tab === "calibrated" ? "active" : ""}`} onClick={() => setTab("calibrated")} data-testid="tab-calibrated">Calibrated Equipment</button>
          <button role="tab" aria-selected={tab === "cleaning"} className={`vb-tab ${tab === "cleaning" ? "active" : ""}`} onClick={() => setTab("cleaning")} data-testid="tab-cleaning">Cleaning & Maintenance</button>
        </div>
        {tab === "types" && (
          <button className="vb-btn" onClick={openType} data-testid="add-type"><Plus size={16} /> Add equipment type</button>
        )}
        {tab === "equipment" && (
          <button className="vb-btn" onClick={openEq} disabled={!sites.length || !types.length} data-testid="add-eq"><Plus size={16} /> Add equipment</button>
        )}
        {tab === "calibrated" && (
          <button className="vb-btn" onClick={openCal} disabled={!sites.length} data-testid="add-cal"><Plus size={16} /> Add calibrated equipment</button>
        )}
      </div>

      {tab === "types" && (
        <div className="vb-card overflow-x-auto">
          <table className="vb-table">
            <thead><tr><th>Name</th><th>Description</th><th>Calibration</th><th>Operating range</th><th>Captures/day</th><th></th></tr></thead>
            <tbody>
              {types.length === 0 && <tr><td colSpan={6} style={{ textAlign: "center", padding: 28 }} className="opacity-60">No types defined yet — start by adding a "Walk-in Freezer", "Thermometer", etc.</td></tr>}
              {types.map((t) => {
                const unit = t.calibration_interval_unit || "days";
                const val = unit === "months"
                  ? Math.max(1, Math.round((t.default_calibration_interval_days || 0) / 30))
                  : (t.default_calibration_interval_days || 0);
                return (
                  <tr key={t.id}>
                    <td><strong>{t.name}</strong></td>
                    <td>{t.description || "—"}</td>
                    <td>{t.requires_calibration
                      ? <span className="vb-pill warn">Every {val} {unit === "months" ? (val === 1 ? "month" : "months") : (val === 1 ? "day" : "days")}</span>
                      : <span className="vb-pill">No</span>}</td>
                    <td>{t.has_operating_range
                      ? <span className="vb-pill">{t.parameter_name || "Range"}: {t.min_value ?? "?"}–{t.max_value ?? "?"} {t.parameter_unit || ""}</span>
                      : <span className="opacity-50">—</span>}</td>
                    <td className="font-mono text-sm">{t.collection_frequency_per_day ?? 1}/day</td>
                    <td>
                      <div className="flex gap-1 justify-end">
                        <button className="vb-btn ghost" onClick={() => editType(t)} data-testid={`edit-type-${t.id}`} title="Edit"><Pencil size={14} /></button>
                        <button className="vb-btn ghost" onClick={() => del("type", t.id, t.name)} data-testid={`del-type-${t.id}`} title="Delete"><Trash2 size={14} /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {tab === "equipment" && (
        <div>
          {(!sites.length || !types.length) && <p className="text-sm opacity-70 mb-3">Add at least one site and one equipment type first.</p>}
          <div className="flex items-center justify-end mb-2">
            <label className="text-[12px] opacity-70 flex items-center gap-1.5 cursor-pointer"
              data-testid="eq-show-archived-toggle">
              <input type="checkbox" checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
                data-testid="eq-show-archived-input" />
              Show archived
            </label>
          </div>
          <div className="vb-card overflow-x-auto">
            <table className="vb-table">
              <thead><tr>
                {isAllSites && <th>Site</th>}
                <th>Name</th><th>Type</th><th>Facility</th><th>Serial / Model</th><th>Last calibrated</th><th>Next due</th><th></th>
              </tr></thead>
              <tbody>
                {items.length === 0 && <tr><td colSpan={isAllSites ? 8 : 7} style={{ textAlign: "center", padding: 28 }} className="opacity-60">{showArchived ? "No equipment found." : "No equipment registered."}</td></tr>}
                {items.map((e) => (
                  <tr key={e.id} style={e.is_archived ? { opacity: 0.55 } : undefined} data-testid={`eq-row-${e.id}`}>
                    {isAllSites && <td className="opacity-70 text-sm">{siteName(e.site_id)}</td>}
                    <td>
                      <strong>{e.name}</strong>
                      {e.is_archived && (
                        <span className="vb-pill ml-2" style={{ background: "var(--vb-bg-2,#faf6f1)" }}
                          data-testid={`eq-archived-pill-${e.id}`}>
                          <Archive size={11} /> Archived
                        </span>
                      )}
                    </td>
                    <td>{typeName(e.equipment_type_id)}</td>
                    <td className="opacity-70 text-sm">{e.facility_id ? facilityName(e.facility_id) : <em className="opacity-50">—</em>}</td>
                    <td className="opacity-70">
                      <div>{e.serial_number || "—"}</div>
                      <div className="text-xs">{e.model_number || ""}</div>
                    </td>
                    <td>
                      <div>{e.last_calibrated_at ? new Date(e.last_calibrated_at).toLocaleDateString() : "—"}</div>
                      {e.last_calibrated_by && <div className="opacity-60 text-xs">by {e.last_calibrated_by}</div>}
                    </td>
                    <td>{e.next_calibration_due
                      ? <span className={`vb-pill ${isOverdue(e.next_calibration_due) ? "err" : "warn"}`}><Calendar size={12} /> {new Date(e.next_calibration_due).toLocaleDateString()}</span>
                      : "—"}</td>
                    <td>
                      <div className="flex gap-1 justify-end items-center">
                        {e.is_archived ? (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button className="vb-btn ghost" data-testid={`eq-actions-${e.id}`} title="Actions"><MoreHorizontal size={16} /></button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" style={{ backgroundColor: "var(--vb-card)", border: "1px solid var(--vb-border)", color: "var(--vb-text-2)", boxShadow: "0 10px 30px rgba(120,61,25,0.18)" }}>
                              <DropdownMenuItem onClick={() => unarchiveEquipment(e)} data-testid={`unarchive-eq-${e.id}`}>
                                <ArchiveRestore size={14} className="mr-2" /> Restore (un-archive)
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => del("eq", e.id, e.name)} data-testid={`del-eq-${e.id}`} className="text-red-600 focus:text-red-600">
                                <Trash2 size={14} className="mr-2" /> Delete permanently
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        ) : (
                          <>
                            <button className="vb-btn ghost" onClick={() => setQrEquipment(e)} data-testid={`qr-eq-${e.id}`} title="Generate QR label"><QrCode size={14} /></button>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <button className="vb-btn ghost" data-testid={`eq-actions-${e.id}`} title="More actions"><MoreHorizontal size={16} /></button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" style={{ backgroundColor: "var(--vb-card)", border: "1px solid var(--vb-border)", color: "var(--vb-text-2)", boxShadow: "0 10px 30px rgba(120,61,25,0.18)" }}>
                                <DropdownMenuItem onClick={() => editEq(e)} data-testid={`edit-eq-${e.id}`}>
                                  <Pencil size={14} className="mr-2" /> Edit
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => archiveEquipment(e)} data-testid={`archive-eq-${e.id}`}>
                                  <Archive size={14} className="mr-2" /> Archive (keep history)
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => del("eq", e.id, e.name)} data-testid={`del-eq-${e.id}`} className="text-red-600 focus:text-red-600">
                                  <Trash2 size={14} className="mr-2" /> Delete permanently
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "calibrated" && (
        <div>
          {!sites.length && <p className="text-sm opacity-70 mb-3">Add at least one site first.</p>}
          <div className="flex items-center justify-end mb-2">
            <label className="text-[12px] opacity-70 flex items-center gap-1.5 cursor-pointer"
              data-testid="cal-show-archived-toggle">
              <input type="checkbox" checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
                data-testid="cal-show-archived-input" />
              Show archived
            </label>
          </div>
          <div className="vb-card overflow-x-auto">
            <table className="vb-table" data-testid="calibrated-table">
              <thead><tr>
                {isAllSites && <th>Site</th>}
                <th>Name</th><th>Facility</th><th>Serial / Model</th><th>Interval</th><th>Last calibrated</th><th>Next due</th><th></th>
              </tr></thead>
              <tbody>
                {calItems.length === 0 && <tr><td colSpan={isAllSites ? 8 : 7} style={{ textAlign: "center", padding: 28 }} className="opacity-60">No calibrated equipment registered yet — add a calibration thermometer, test weight, etc.</td></tr>}
                {calItems.map((c) => {
                  const unit = c.calibration_interval_unit || "months";
                  const v = c.calibration_interval_value || 0;
                  return (
                    <tr key={c.id} data-testid={`cal-row-${c.id}`}
                      className={c.is_archived ? "opacity-60" : ""}>
                      {isAllSites && <td className="opacity-70 text-sm">{siteName(c.site_id)}</td>}
                      <td>
                        <strong>{c.name}</strong>
                        {c.is_archived && (
                          <span className="vb-pill ml-2" style={{ fontSize: 10 }}>
                            <Archive size={10} /> Archived
                          </span>
                        )}
                      </td>
                      <td className="opacity-70 text-sm">{c.facility_id ? facilityName(c.facility_id) : <em className="opacity-50">—</em>}</td>
                      <td className="opacity-70">
                        <div>{c.serial_number || "—"}</div>
                        <div className="text-xs">{c.model_number || ""}</div>
                      </td>
                      <td><span className="vb-pill">Every {v} {unit === "months" ? (v === 1 ? "month" : "months") : (v === 1 ? "day" : "days")}</span></td>
                      <td>
                        <div>{c.last_calibrated_at ? new Date(c.last_calibrated_at).toLocaleDateString() : "—"}</div>
                        {c.last_calibrated_by && <div className="opacity-60 text-xs">by {c.last_calibrated_by}</div>}
                        {c.calibration_certificate_url && (
                          <a href={c.calibration_certificate_url}
                             target="_blank" rel="noreferrer"
                             className="text-xs underline"
                             data-testid={`cal-cert-link-${c.id}`}>
                            View certificate
                          </a>
                        )}
                      </td>
                      <td>{c.next_calibration_due
                        ? <span className={`vb-pill ${isOverdue(c.next_calibration_due) ? "err" : "warn"}`}><Calendar size={12} /> {new Date(c.next_calibration_due).toLocaleDateString()}</span>
                        : "—"}</td>
                      <td>
                        <div className="flex gap-1 justify-end">
                          {c.is_archived ? (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <button className="vb-btn ghost" data-testid={`cal-actions-${c.id}`} title="More actions"><MoreHorizontal size={16} /></button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" style={{ backgroundColor: "var(--vb-card)", border: "1px solid var(--vb-border)", color: "var(--vb-text-2)", boxShadow: "0 10px 30px rgba(120,61,25,0.18)" }}>
                                <DropdownMenuItem onClick={() => unarchiveCal(c)} data-testid={`unarchive-cal-${c.id}`}>
                                  <ArchiveRestore size={14} className="mr-2" /> Restore
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => del("cal", c.id, c.name)} data-testid={`del-cal-${c.id}`} className="text-red-600 focus:text-red-600">
                                  <Trash2 size={14} className="mr-2" /> Delete permanently
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          ) : (
                            <>
                              <button className="vb-btn ghost" onClick={() => editCal(c)} data-testid={`edit-cal-${c.id}`} title="Edit / Mark calibrated"><Pencil size={14} /></button>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <button className="vb-btn ghost" data-testid={`cal-actions-${c.id}`} title="More actions"><MoreHorizontal size={16} /></button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" style={{ backgroundColor: "var(--vb-card)", border: "1px solid var(--vb-border)", color: "var(--vb-text-2)", boxShadow: "0 10px 30px rgba(120,61,25,0.18)" }}>
                                  <DropdownMenuItem onClick={() => archiveCal(c)} data-testid={`archive-cal-${c.id}`}>
                                    <Archive size={14} className="mr-2" /> Archive (keep history)
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => del("cal", c.id, c.name)} data-testid={`del-cal-${c.id}`} className="text-red-600 focus:text-red-600">
                                    <Trash2 size={14} className="mr-2" /> Delete permanently
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "cleaning" && (
        <CleaningLogTab equipment={items} />
      )}

      {show && createPortal(
        <div className="vb-modal-bg" onClick={(e) => { if (e.target === e.currentTarget) setShow(null); }}>
          <div className="vb-modal">
            <h2>
              {show === "type"
                ? (editingId ? "Edit equipment type" : "Add equipment type")
                : show === "cal"
                ? (editingId ? "Edit calibrated equipment" : "Add calibrated equipment")
                : (editingId ? "Edit equipment" : "Add equipment")}
            </h2>
            {err && <div className="vb-pill err mb-3">{err}</div>}
            <form onSubmit={submit} className="grid gap-3">
              {show === "type" ? (
                <>
                  {/* Quick-fill from the curated preset list. Picking one
                      autofills name + parameter range + interval defaults
                      but the owner can still tweak everything below. The
                      requires-calibration checkbox stays user-controlled. */}
                  {!editingId && (
                    <div className="rounded-lg p-3" style={{ background: "var(--vb-bg)", border: "1px dashed var(--vb-border)" }}>
                      <label className="text-xs font-semibold uppercase tracking-wider block mb-1.5" style={{ color: "var(--vb-text-2)" }}>
                        Start from a preset
                      </label>
                      <select className="vb-input"
                        value=""
                        onChange={(e) => {
                          const p = findPreset(e.target.value);
                          if (!p) return;
                          // Non-temp presets (Band Saw, Delivery Truck) turn
                          // OFF the operating range block entirely — the form
                          // hides parameter/min/max/freq when has_operating_range
                          // is false, so tenants stop hitting the "range required"
                          // trap on cleanliness-only equipment.
                          const hasRange = p.has_operating_range !== false;
                          setForm({
                            ...form,
                            name: p.name,
                            description: p.desc,
                            requires_calibration: !!p.requires_calibration,
                            _intervalValue: 12,
                            calibration_interval_unit: "months",
                            default_calibration_interval_days: 365,
                            has_operating_range: hasRange,
                            parameter_name: hasRange ? p.parameter : "",
                            parameter_unit: hasRange ? p.unit : "",
                            min_value: hasRange ? p.min : null,
                            max_value: hasRange ? p.max : null,
                            collection_frequency_per_day: hasRange ? (p.freq || 1) : 0,
                          });
                        }}
                        data-testid="type-preset-picker">
                        <option value="">— Pick a preset to auto-fill —</option>
                        {EQUIPMENT_TYPE_PRESETS.map((p) => (
                          <option key={p.name} value={p.name}>{p.name} ({p.label_hint})</option>
                        ))}
                      </select>
                      <p className="text-[11px] opacity-60 mt-1.5 m-0">
                        Or type a custom name below — leave the preset blank if you have niche gear (ovens, blast chillers, pH meters, etc.).
                      </p>
                    </div>
                  )}
                  <input className="vb-input" required placeholder="Name (e.g. Walk-in Freezer)" value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="type-name" />
                  <textarea className="vb-input" rows={2} placeholder="Description" value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="type-description" />
                  <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!form.requires_calibration} onChange={(e) => setForm({ ...form, requires_calibration: e.target.checked })} data-testid="type-req-cal" /> Requires calibration</label>
                  {form.requires_calibration && (
                    <div>
                      <label className="text-xs opacity-70 block mb-1">Calibration interval</label>
                      <div className="grid grid-cols-[1fr_140px] gap-2">
                        <input className="vb-input" type="number" min={1} required placeholder="Interval" value={form._intervalValue ?? ""} onChange={(e) => setForm({ ...form, _intervalValue: e.target.value === "" ? "" : Number(e.target.value) })} data-testid="type-interval-value" />
                        <select className="vb-input" value={form.calibration_interval_unit || "days"} onChange={(e) => setForm({ ...form, calibration_interval_unit: e.target.value })} data-testid="type-interval-unit">
                          <option value="days">Days</option>
                          <option value="months">Months</option>
                        </select>
                      </div>
                    </div>
                  )}
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox"
                      checked={form.has_operating_range !== false}
                      onChange={(e) => setForm({ ...form, has_operating_range: e.target.checked,
                        // Reset numeric fields when turning OFF so no
                        // stale min/max ships with a cleanliness-only type.
                        ...(e.target.checked ? {} : {
                          parameter_name: "", parameter_unit: "",
                          min_value: null, max_value: null,
                          collection_frequency_per_day: 0,
                        }),
                      })}
                      data-testid="type-has-range" />
                    Requires temperature / operating-range monitoring
                    <span className="text-[11px] opacity-60">(uncheck for cleanliness-only items like Band Saw or Delivery Truck)</span>
                  </label>
                  {form.has_operating_range !== false && (
                  <div className="rounded-lg p-3 mt-1" style={{ background: "var(--vb-bg)" }}>
                    <div className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: "var(--vb-text-2)" }}>
                      Operating parameters <span className="opacity-60">*</span>
                    </div>
                    <p className="text-[11px] opacity-70 mb-3">Required — these define the acceptable range for readings against this equipment. For non-measurement assets (e.g. calibration thermometers), use the <strong>Calibrated Equipment</strong> tab instead.</p>
                    <div className="grid gap-2">
                      <div className="grid grid-cols-2 gap-2">
                        <input className="vb-input" required placeholder="Parameter name (e.g. Temperature)" value={form.parameter_name || ""} onChange={(e) => setForm({ ...form, parameter_name: e.target.value })} data-testid="type-param-name" />
                        <input className="vb-input" required placeholder="Unit (e.g. °C)" value={form.parameter_unit || ""} onChange={(e) => setForm({ ...form, parameter_unit: e.target.value })} data-testid="type-param-unit" />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <input className="vb-input" type="number" step="any" required placeholder="Min value" value={form.min_value ?? ""} onChange={(e) => setForm({ ...form, min_value: e.target.value })} data-testid="type-min" />
                        <input className="vb-input" type="number" step="any" required placeholder="Max value" value={form.max_value ?? ""} onChange={(e) => setForm({ ...form, max_value: e.target.value })} data-testid="type-max" />
                      </div>
                      <div>
                        <label className="text-xs opacity-70 block mb-1">
                          Data collection frequency (readings per day) <span className="opacity-60">*</span>
                        </label>
                        <input className="vb-input" type="number" min={1} step={1} required
                          placeholder="e.g. 2"
                          value={form.collection_frequency_per_day ?? 1}
                          onChange={(e) => setForm({ ...form, collection_frequency_per_day: e.target.value === "" ? 1 : Math.max(1, parseInt(e.target.value, 10) || 1) })}
                          data-testid="type-freq-per-day" />
                        <p className="text-[11px] opacity-60 mt-1 m-0">
                          Anything less than this on a given day is flagged as incomplete on the CCP - Data Capture page.
                        </p>
                      </div>
                    </div>
                  </div>
                  )}
                </>
              ) : show === "cal" ? (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs opacity-70 block mb-1">Site <span className="opacity-60">*</span></label>
                      {isSingleSite ? (
                        <div className="vb-input flex items-center gap-2 opacity-90" data-testid="cal-site-locked">
                          <span style={{ fontSize: 12 }}>📍</span> {siteName(form.site_id)}
                        </div>
                      ) : (
                        <select className="vb-input" required value={form.site_id || ""} onChange={(e) => setForm({ ...form, site_id: e.target.value, facility_id: "" })} data-testid="cal-site">
                          <option value="">Select site</option>
                          {(visibleSites.length ? visibleSites : sites).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                        </select>
                      )}
                    </div>
                    <div>
                      <label className="text-xs opacity-70 block mb-1">Facility <span className="opacity-60">*</span></label>
                      <select className="vb-input" required value={form.facility_id || ""} onChange={(e) => setForm({ ...form, facility_id: e.target.value })} data-testid="cal-facility" disabled={!form.site_id || siteFacilities.length === 0}>
                        <option value="">{siteFacilities.length === 0 ? "No facilities — add one in Sites" : "Select facility"}</option>
                        {siteFacilities.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
                      </select>
                    </div>
                  </div>
                  <input className="vb-input" required placeholder="Name of calibrated equipment (e.g. Calibration Thermometer)" value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="cal-name" />
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs opacity-70 block mb-1">Serial number <span className="opacity-60">*</span></label>
                      <input className="vb-input" required placeholder="Serial number" value={form.serial_number || ""} onChange={(e) => setForm({ ...form, serial_number: e.target.value })} data-testid="cal-serial" />
                    </div>
                    <div>
                      <label className="text-xs opacity-70 block mb-1">Model number <span className="opacity-50">(optional)</span></label>
                      <input className="vb-input" placeholder="Model number" value={form.model_number || ""} onChange={(e) => setForm({ ...form, model_number: e.target.value })} data-testid="cal-model" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs opacity-70 block mb-1">Last calibrated date <span className="opacity-60">*</span></label>
                      <input className="vb-input" type="date" required value={form.last_calibrated_at || ""} onChange={(e) => setForm({ ...form, last_calibrated_at: e.target.value })} data-testid="cal-last-date" />
                    </div>
                    <div>
                      <label className="text-xs opacity-70 block mb-1">Calibrated by <span className="opacity-50">(optional)</span></label>
                      <input className="vb-input" placeholder="e.g. ABC Calibration Lab" value={form.last_calibrated_by || ""} onChange={(e) => setForm({ ...form, last_calibrated_by: e.target.value })} data-testid="cal-cal-by" />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs opacity-70 block mb-1">Calibration interval <span className="opacity-60">*</span></label>
                    <div className="grid grid-cols-[1fr_140px] gap-2">
                      <input className="vb-input" type="number" min={1} required placeholder="Interval" value={form.calibration_interval_value ?? ""} onChange={(e) => setForm({ ...form, calibration_interval_value: e.target.value === "" ? "" : Number(e.target.value) })} data-testid="cal-interval-value" />
                      <select className="vb-input" value={form.calibration_interval_unit || "months"} onChange={(e) => setForm({ ...form, calibration_interval_unit: e.target.value })} data-testid="cal-interval-unit">
                        <option value="days">Days</option>
                        <option value="months">Months</option>
                      </select>
                    </div>
                    {form.last_calibrated_at && form.calibration_interval_value && (
                      <p className="text-[11px] opacity-60 mt-1">
                        Next due: <strong>{(() => {
                          const d = new Date(form.last_calibrated_at);
                          const days = (form.calibration_interval_unit === "months" ? 30 : 1) * Number(form.calibration_interval_value);
                          d.setDate(d.getDate() + days);
                          return d.toLocaleDateString();
                        })()}</strong>
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="text-xs opacity-70 block mb-1">Calibration certificate <span className="opacity-50">(optional — PDF or image)</span></label>
                    <div className="flex flex-wrap gap-2 items-center">
                      {form.calibration_certificate_url ? (
                        <>
                          <a href={form.calibration_certificate_url}
                             target="_blank" rel="noreferrer"
                             className="vb-btn ghost sm"
                             data-testid="cal-cert-view">
                            View uploaded
                          </a>
                          <button type="button" className="vb-btn ghost sm"
                            onClick={() => setForm({ ...form, calibration_certificate_url: "" })}
                            data-testid="cal-cert-remove">
                            <Trash2 size={12} /> Remove
                          </button>
                        </>
                      ) : (
                        <label className="vb-btn ghost sm" style={{ cursor: "pointer" }}
                          data-testid="cal-cert-upload">
                          Upload certificate
                          <input type="file" accept="application/pdf,image/*" hidden
                            onChange={async (ev) => {
                              const file = ev.target.files?.[0];
                              if (!file) return;
                              try {
                                const fd = new FormData();
                                fd.append("file", file);
                                const { data } = await api.post(
                                  "/calibrated-equipment/upload-certificate",
                                  fd, { headers: { "Content-Type": "multipart/form-data" } },
                                );
                                setForm((f) => ({ ...f, calibration_certificate_url: data.url }));
                              } catch (e) {
                                setErr(formatErr(e.response?.data?.detail) || "Upload failed");
                              }
                            }} />
                        </label>
                      )}
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs opacity-70 block mb-1">Site</label>
                      {isSingleSite ? (
                        <div className="vb-input flex items-center gap-2 opacity-90" data-testid="eq-site-locked">
                          <span style={{ fontSize: 12 }}>📍</span> {siteName(form.site_id)}
                        </div>
                      ) : (
                        <select className="vb-input" required value={form.site_id || ""} onChange={(e) => setForm({ ...form, site_id: e.target.value, facility_id: "" })} data-testid="eq-site">
                          <option value="">Select site</option>
                          {(visibleSites.length ? visibleSites : sites).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                        </select>
                      )}
                    </div>
                    <div>
                      <label className="text-xs opacity-70 block mb-1">
                        Facility {!isVehicle && <span className="opacity-60">*</span>}
                      </label>
                      {isVehicle ? (
                        <div className="vb-input flex items-center gap-2 opacity-80 text-sm"
                          style={{ background: "var(--vb-bg-2, #faf6f1)" }}
                          data-testid="eq-facility-vehicle-notice">
                          <span style={{ fontSize: 12 }}>🚚</span>
                          Vehicles live at a site — no facility needed.
                        </div>
                      ) : (
                        <select className="vb-input" required value={form.facility_id || ""} onChange={(e) => setForm({ ...form, facility_id: e.target.value })} data-testid="eq-facility" disabled={!form.site_id || siteFacilities.length === 0}>
                          <option value="">{siteFacilities.length === 0 ? "No facilities — add one in Sites" : "Select facility"}</option>
                          {siteFacilities.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
                        </select>
                      )}
                    </div>
                  </div>
                  <div>
                    <label className="text-xs opacity-70 block mb-1">Equipment type</label>
                    <select className="vb-input" required value={form.equipment_type_id || ""} onChange={(e) => setForm({ ...form, equipment_type_id: e.target.value })} data-testid="eq-type">
                      <option value="">Select type</option>
                      {types.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                  </div>
                  {selectedType?.has_operating_range && (
                    <div className="vb-pill" style={{ alignSelf: "flex-start" }}>
                      Operating range: {selectedType.parameter_name || "—"} {selectedType.min_value ?? "?"}–{selectedType.max_value ?? "?"} {selectedType.parameter_unit || ""}
                    </div>
                  )}
                  {selectedType?.has_operating_range ? (
                  <div className="rounded-lg p-3" style={{ background: "var(--vb-bg)" }} data-testid="eq-monitoring-mode">
                    <div className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: "var(--vb-text-2)" }}>
                      Monitoring mode
                    </div>
                    <label className="flex items-start gap-2 mb-2 cursor-pointer">
                      <input type="radio" name="eq-mode" className="mt-1"
                        checked={(form.monitoring_mode || "scheduled") === "scheduled"}
                        onChange={() => setForm({ ...form, monitoring_mode: "scheduled" })}
                        data-testid="eq-mode-scheduled" />
                      <div>
                        <div className="text-sm font-medium" style={{ color: "var(--vb-text-2)" }}>Scheduled capture</div>
                        <p className="text-[11px] opacity-70 m-0">Staff manually log readings each day (classic). Missing days are flagged on CCP - Data Capture.</p>
                      </div>
                    </label>
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input type="radio" name="eq-mode" className="mt-1"
                        checked={form.monitoring_mode === "exception"}
                        onChange={() => setForm({ ...form, monitoring_mode: "exception" })}
                        data-testid="eq-mode-exception" />
                      <div>
                        <div className="text-sm font-medium" style={{ color: "var(--vb-text-2)" }}>Exception capture (external IoT logger)</div>
                        <p className="text-[11px] opacity-70 m-0">An external data-logger monitors temperature continuously. Staff only record deviations when the logger alarms — no scheduled reading target, and clean days are never flagged as missing.</p>
                      </div>
                    </label>
                  </div>
                  ) : (
                    <div
                      className="rounded-lg p-3 text-xs opacity-80"
                      style={{ background: "var(--vb-bg)", color: "var(--vb-text-2)" }}
                      data-testid="eq-no-monitoring-note"
                    >
                      This equipment type has no operating range — no daily
                      temperature readings are required. Track cleaning &amp;
                      maintenance events (blade changes, sanitation, etc.) via
                      the Cleaning &amp; Maintenance tab or by scanning the
                      equipment&apos;s QR code.
                    </div>
                  )}
                  <input className="vb-input" required
                    placeholder={isVehicle ? "Vehicle nickname (e.g. Truck-01)" : "Equipment name (e.g. Freezer #1)"}
                    value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })}
                    data-testid="eq-name" />
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs opacity-70 block mb-1">
                        {isVehicle ? "Vehicle number" : "Serial number"} <span className="opacity-60">*</span>
                      </label>
                      <input className="vb-input" required
                        placeholder={isVehicle ? "Vehicle registration / plate number" : "Serial number"}
                        value={form.serial_number || ""}
                        onChange={(e) => setForm({ ...form, serial_number: e.target.value })}
                        data-testid="eq-serial" />
                    </div>
                    <div>
                      <label className="text-xs opacity-70 block mb-1">Model number <span className="opacity-50">(optional)</span></label>
                      <input className="vb-input" placeholder="Model number" value={form.model_number || ""} onChange={(e) => setForm({ ...form, model_number: e.target.value })} data-testid="eq-model" />
                    </div>
                  </div>
                  {selectedType?.requires_calibration && (
                    <>
                      <div className="border-t pt-3 mt-1"><span className="text-xs opacity-70 uppercase tracking-wide">Calibration</span></div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs opacity-70 block mb-1">Last calibrated on</label>
                          <input className="vb-input" type="date" value={form.last_calibrated_at || ""} onChange={(e) => setForm({ ...form, last_calibrated_at: e.target.value })} data-testid="eq-last-cal" />
                        </div>
                        <div>
                          <label className="text-xs opacity-70 block mb-1">Calibrated by</label>
                          <input className="vb-input" placeholder="e.g. ABC Calibration Lab" value={form.last_calibrated_by || ""} onChange={(e) => setForm({ ...form, last_calibrated_by: e.target.value })} data-testid="eq-cal-by" />
                        </div>
                      </div>
                      <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={!!form.next_due_manual_override} onChange={(e) => setForm({ ...form, next_due_manual_override: e.target.checked })} data-testid="eq-override-due" />
                        Override next due date
                      </label>
                      {form.next_due_manual_override ? (
                        <div>
                          <label className="text-xs opacity-70 block mb-1">Next due (override)</label>
                          <input className="vb-input" type="date" value={form.next_calibration_due || ""} onChange={(e) => setForm({ ...form, next_calibration_due: e.target.value })} data-testid="eq-next-due" />
                        </div>
                      ) : (
                        <div className="text-xs opacity-70">
                          {previewNextDue
                            ? <>Next due will auto-compute to <strong>{previewNextDue}</strong> ({selectedType.default_calibration_interval_days} days from last calibrated).</>
                            : <>Enter "Last calibrated" to auto-compute next due.</>}
                        </div>
                      )}
                      <input className="vb-input" placeholder="Calibration certificate URL (optional)" value={form.calibration_certificate_url || ""} onChange={(e) => setForm({ ...form, calibration_certificate_url: e.target.value })} data-testid="eq-cert-url" />
                    </>
                  )}
                </>
              )}
              <div className="flex gap-2 justify-end">
                <button type="button" className="vb-btn ghost" onClick={() => setShow(null)}>Cancel</button>
                <button type="submit" className="vb-btn" data-testid={show === "cal" ? "cal-submit" : show === "type" ? "type-submit" : "eq-submit"}>Save</button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {qrEquipment && (
        <EquipmentQRDialog
          equipment={qrEquipment}
          siteName={siteName(qrEquipment.site_id)}
          facilityName={facilityName(qrEquipment.facility_id)}
          onClose={() => setQrEquipment(null)}
        />
      )}
    </div>
  );
}
